import classes from './settings.module.scss';
import { classNames } from '../../utils';
import useWindowDimensions from '../../Hooks/window-dimensions.hook';
import { useLocation, useNavigate, Outlet, useParams } from 'react-router-dom';
import useProfile from '../../Hooks/settings/profile.hook';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Breadcrumb, Menu, MenuProps } from 'antd';
import { RiLockPasswordFill } from 'react-icons/ri';
import { MdChevronRight } from 'react-icons/md';
import { AiFillHome } from "react-icons/ai";
import { FaUserFriends } from "react-icons/fa";
import { IoPersonSharp } from 'react-icons/io5';

type MenuItem = Required<MenuProps>['items'][number];
type ISection = 'profile' | 'update-password' | 'user-management';
interface ISectionDetails {
  title: string;
  isFullWidth?: boolean;
}

const DEFAULT_SECTION_PATH = 'profile';

const getAdminMenuItems = (t: (key: string) => string): MenuItem[] => [
  {
    label: t('settings.admin-settings'),
    key: 'admin-settings',
    type: 'group',
    children: [
      {
        label: t('settings.sections.user-management'),
        key: 'user-management',
        icon: <FaUserFriends />
      }
    ]
  }
]

const getMenuItems = (t: (key: string) => string): MenuItem[] => [
  {
    label: t('settings.account-settings'),
    key: 'account-settings',
    type: 'group',
    children: [
      {
        label: t('settings.sections.profile'),
        key: 'profile',
        icon: <IoPersonSharp />
      },
      {
        label: t('settings.sections.update-password'),
        key: 'update-password',
        icon: <RiLockPasswordFill />
      }
    ]
  }
]

const SECTION_DETAILS: Record<ISection, ISectionDetails> = {
  'profile': { title: 'Profile' },
  'update-password': { title: 'Update Password' },
  'user-management': { title: 'User Management', isFullWidth: true }
}

export const Settings = () => {
  const { t } = useTranslation();
  const profile = useProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const { locale = 'en' } = useParams<{ locale: string }>();
  const { width } = useWindowDimensions();
  const [currentSection, setCurrentSection] = useState<ISection>();

  useEffect(() => {
    const pathSegments = location.pathname.split('/');
    const sectionSegment = pathSegments?.[3]

    if (!!sectionSegment && Object.keys(SECTION_DETAILS).includes(sectionSegment)) {
      setCurrentSection(location.pathname.split('/')[3] as ISection)
    } else {
      navigate(`/${locale}/settings/${DEFAULT_SECTION_PATH}`);
    }
  }, [location, locale, navigate]);

  if (!currentSection) {
    return null;
  }

  const currentSectionDetails = SECTION_DETAILS[currentSection];
  const menuItems = getMenuItems(t);
  const adminMenuItems = getAdminMenuItems(t);

  return (
    <div className={classes.wrapper}>
      <Menu
        onClick={e => navigate(`/${locale}/settings/${e.key}`)}
        mode="vertical"
        selectedKeys={[currentSection]}
        inlineCollapsed={width <= 1024}
        items={profile.userInfo.value?.role === 'ADMIN' ? [...menuItems, ...adminMenuItems] : menuItems}
      />
      <main className={classNames(classes.main, { [classes.full]: currentSectionDetails.isFullWidth })}>
        <div className={classes['page-info']}>
          <Breadcrumb
            separator={<MdChevronRight size={16} />}
            items={[
              {
                title: <AiFillHome />,
                href: `/${locale}/settings/${DEFAULT_SECTION_PATH}`
              },
              {
                title: t('settings.title')
              },
              {
                title: t(`settings.sections.${currentSection}`)
              }
            ]}
          />
          <h1>{t(`settings.sections.${currentSection}`)}</h1>
        </div>
        <Outlet />
      </main>
    </div>
  );
};
